<script>
import { Icon, divIcon, latLngBounds } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const iconCache = {};

function densityIcon(count) {
  const key = count > 999 ? "999+" : String(count);
  if (iconCache[key]) return iconCache[key];

  const size = count === 1 ? 28 : count < 10 ? 36 : count < 50 ? 44 : count < 200 ? 52 : 60;
  const bg = count === 1
    ? "#2563eb"
    : count < 10
      ? "#16a34a"
      : count < 50
        ? "#ca8a04"
        : count < 200
          ? "#ea580c"
          : "#dc2626";

  const label = count === 1 ? "" : (count > 999 ? "999+" : String(count));
  const icon = divIcon({
    className: "sub-density-icon",
    html: `<div class="sub-density-bubble" style="width:${size}px;height:${size}px;background:${bg};">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

  iconCache[key] = icon;
  return icon;
}

function gridStep(zoom) {
  if (zoom <= 8) return 0.45;
  if (zoom <= 9) return 0.28;
  if (zoom <= 10) return 0.16;
  if (zoom <= 11) return 0.09;
  if (zoom <= 12) return 0.05;
  if (zoom <= 13) return 0.025;
  if (zoom <= 14) return 0.012;
  if (zoom <= 15) return 0.006;
  return 0;
}

export default {
  name: "SubscriptionMapModal",
  components: { LMap, LTileLayer, LMarker, LPopup },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      zoom: 10,
      center: [35.1856, 33.3823],
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      mapZoom: 10,
    };
  },
  computed: {
    markers() {
      const list = [];
      const items = this.items || [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const latlng = this.parseCoordinate(item.coordinate);
        if (!latlng) continue;
        list.push({
          id: item.id,
          username: item.username,
          fullname: item.fullname,
          address: item.address,
          latlng,
        });
      }
      return list;
    },
    clusters() {
      const step = gridStep(this.mapZoom);
      const markers = this.markers;

      if (!markers.length) return [];

      // Yüksek zoom: tek tek (max 300 görünür performans için)
      if (step === 0) {
        return markers.slice(0, 400).map(m => ({
          id: `p-${m.id}`,
          latlng: m.latlng,
          count: 1,
          item: m,
          icon: densityIcon(1),
        }));
      }

      const buckets = Object.create(null);
      for (let i = 0; i < markers.length; i++) {
        const m = markers[i];
        const gy = Math.floor(m.latlng[0] / step);
        const gx = Math.floor(m.latlng[1] / step);
        const key = gy + ":" + gx;
        let b = buckets[key];
        if (!b) {
          b = buckets[key] = { sumLat: 0, sumLng: 0, count: 0, sample: m, key };
        }
        b.sumLat += m.latlng[0];
        b.sumLng += m.latlng[1];
        b.count += 1;
        if (b.count === 1) b.sample = m;
      }

      const out = [];
      const keys = Object.keys(buckets);
      for (let i = 0; i < keys.length; i++) {
        const b = buckets[keys[i]];
        const count = b.count;
        out.push({
          id: "c-" + b.key,
          latlng: [b.sumLat / count, b.sumLng / count],
          count,
          item: count === 1 ? b.sample : null,
          icon: densityIcon(count),
        });
      }
      return out;
    },
  },
  watch: {
    markers(val) {
      if (val.length) {
        this.$nextTick(() => this.onShown());
      }
    },
  },
  methods: {
    parseCoordinate(raw) {
      if (!raw) return null;
      const parts = String(raw).split(",");
      if (parts.length !== 2) return null;
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return [lat, lng];
    },
    fitBounds() {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map || !this.markers.length) return;

      const bounds = latLngBounds(this.markers.map(m => m.latlng));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      this.mapZoom = map.getZoom();
    },
    onZoomEnd(e) {
      const z = e && e.target ? e.target.getZoom() : null;
      if (z != null) this.mapZoom = z;
    },
    onClusterClick(cluster) {
      if (cluster.count <= 1) return;
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map) return;
      map.setView(cluster.latlng, Math.min(18, map.getZoom() + 2));
      this.mapZoom = map.getZoom();
    },
    onShown() {
      this.$nextTick(() => {
        const map = this.$refs.map && this.$refs.map.mapObject;
        if (map) {
          map.invalidateSize();
          this.fitBounds();
        }
      });
    },
    close() {
      this.$bvModal.hide("subscription_map_modal");
    },
  },
};
</script>

<template>
  <b-modal
    id="subscription_map_modal"
    size="xl"
    title="Haritada Göster"
    hide-footer
    @shown="onShown"
  >
    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin"></i> Harita yükleniyor...
    </div>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span>
          Toplam <strong>{{ markers.length }}</strong> konum ·
          Haritada <strong>{{ clusters.length }}</strong> yoğunluk noktası
        </span>
        <button class="btn btn-secondary btn-sm" @click="close">Kapat</button>
      </div>
      <div v-if="!markers.length" class="alert alert-warning mb-0">
        Seçili filtrelere uygun koordinatlı abone bulunamadı.
      </div>
      <l-map
        v-else
        ref="map"
        :zoom="zoom"
        :center="center"
        style="height: 70vh; width: 100%; z-index: 1;"
        @ready="onShown"
        @zoomend="onZoomEnd"
      >
        <l-tile-layer :url="url" />
        <l-marker
          v-for="cluster in clusters"
          :key="cluster.id"
          :lat-lng="cluster.latlng"
          :icon="cluster.icon"
          @click="onClusterClick(cluster)"
        >
          <l-popup v-if="cluster.count === 1 && cluster.item">
            <strong>{{ cluster.item.fullname }}</strong><br>
            {{ cluster.item.username }}<br>
            <span v-if="cluster.item.address">{{ cluster.item.address }}</span>
          </l-popup>
          <l-popup v-else>
            Bu alanda <strong>{{ cluster.count }}</strong> abone<br>
            <small>Yakınlaştırmak için tıklayın</small>
          </l-popup>
        </l-marker>
      </l-map>
    </div>
  </b-modal>
</template>

<style>
.sub-density-icon {
  background: transparent !important;
  border: none !important;
}

.sub-density-bubble {
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.35);
  line-height: 1;
}
</style>
